import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import NextAction from '../Widget/NextAction';
import Overlay from '../Widget/Overlay';
import OverlayPartner from '../Widget/OverlayPartner';
import OverlayPublisher from '../Widget/OverlayPublisher';
import WidgetHeader from '../Widget/WidgetHeader';

import {
    Body,
    Content,
    Details,
    DetailsContainer,
    Footer,
    PeopleSigned,
    Title as QuestionText
} from '../DefaultStyles/Shared';

import { Container } from '../DefaultStyles/Details';
import { TransitionContainerNoExit } from '../../components/Transition/TranstionContainer';
import ShareDrawer from '../../components/ShareDrawer';
import { fillAnswerPercentage } from './PollAnimations';

import { formatEmoji } from '../../utils/fillDynamicText';
import { isLongText } from '../../components/Shared/commonFunctions.js';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { copyFor, capitalization } from '../../copy/copy';

const Percentage = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    right: 12px;
    font-size: ${props =>
        props.noOfAnswers === 4 ? '18px' : props.noOfAnswers === 3 ? '15px' : '18px'};
    line-height: 1.22;
    font-weight: bold;
    text-align: right;

    @media (max-width: 35em) {
        font-size: 14px;
        right: 6px;
    }

    @media (max-width: 30em) {
        right: 12px;
    }
`;

const AnswerPercentage = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    animation: ${props => fillAnswerPercentage(props.percentage)} 750ms ease-in;
    width: ${props => props.percentage}%;
    height: 100%;
    border-radius: 4px 0 0 4px;
    background: ${props =>
        props.answerAnswered ? 'rgba(255, 36, 94, 0.5)' : 'rgba(255, 36, 94, 0.2)'};
`;

const AnswerText = styled.div`
    position: absolute;
    left: 12px;
    top: 0;
    top: 50%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    transform: translateY(-50%);
    font-size: ${props =>
        props.noOfAnswers === 4 ? '14px' : props.noOfAnswers === 3 ? '13px' : '18px'};
    font-weight: bold;
    color: #000000;
    padding-right: ${props =>
        props.noOfAnswers === 4 ? '58px' : props.noOfAnswers === 3 ? '48px' : '58px'};
    line-height: ${props =>
        props.noOfAnswers === 4 ? '1.12' : props.noOfAnswers === 3 ? '1.15' : '1.18'};
    text-transform: ${props => (props.makeUppercase ? 'uppercase' : 'none')};

    @media (max-width: 35em) {
        font-size: ${props =>
            props.noOfAnswers === 4 ? '13px' : props.noOfAnswers === 3 ? '13px' : '15px'};
        padding-right: 38px;
        left: 6px;
    }

    @media (max-width: 30em) {
        font-size: 13px;
        padding-right: 44px;
        left: 12px;
        line-height: 1.1;
    }

    .emoji {
        font-size: 22px;
    }
`;

const AnswerContent = styled.div`
    width: ${props =>
        props.noOfAnswers === 4
            ? 'calc(50% - 16px)'
            : props.noOfAnswers === 3
            ? 'calc(33.33% - 16px)'
            : 'calc(50% - 16px)'};
    height: ${props => (props.noOfAnswers === 4 ? '55px' : '88px')};
    margin: 0 8px;
    margin-top: ${props => (props.noOfAnswers === 4 ? '10px' : '0')};
    line-height: 100%;
    float: left;
    position: relative;
    border-radius: 4px;
    border: solid 1px #f1f1ee;

    @media (max-width: 30em) {
        width: calc(100% - 15px);
        height: ${props =>
            props.noOfAnswers === 4 ? '44px' : props.noOfAnswers === 3 ? '60px' : '70px'};
        margin-top: 10px;
    }
`;

const AnswersWrapper = styled.div`
    float: left;
    width: calc(100% - 16px);
    position: absolute;
    left: 8px;
    bottom: ${props => (props.hasNextAction ? '104px' : '40px')};
`;

const PollSuccess = props => (
    <TransitionContainerNoExit zIndex={15}>
        <Container>
            <Overlay />
            <OverlayPublisher />
            <OverlayPartner transitionType="right-to-left" />
            <Body isBlurred={false}>
                <WidgetHeader />
                <Content
                    isContactAction={false}
                    useLocalRepresentative={false}
                    isSingleAction={true}
                >
                    <QuestionText
                        isShortText={!isLongText(props.questionText, 28)}
                        isLongText={isLongText(props.questionText, 60)}
                        isExtraLongText={isLongText(props.questionText, 95)}
                        dangerouslySetInnerHTML={{
                            __html: props.questionText
                        }}
                    />
                    <DetailsContainer>
                        <Details>
                            {(!props.hasNextAction || props.isMobileResolution) && (
                                <PeopleSigned>
                                    {props.noOfPeopleAnswered}{' '}
                                    {copyFor(
                                        'miscellaneous',
                                        'numAnswered',
                                        'poll',
                                        capitalization.all
                                    )}
                                </PeopleSigned>
                            )}
                            <AnswersWrapper hasNextAction={props.hasNextAction}>
                                {props.results.map(r => (
                                    <AnswerContent key={r.id} noOfAnswers={r.noOfAnswers}>
                                        <AnswerPercentage
                                            percentage={r.percentage}
                                            answerAnswered={r.answerAnswered}
                                        />
                                        <AnswerText
                                            noOfAnswers={r.noOfAnswers}
                                            makeUppercase={props.makeUppercase}
                                            dangerouslySetInnerHTML={{
                                                __html: formatEmoji(r.text)
                                            }}
                                        />
                                        <Percentage noOfAnswers={r.noOfAnswers}>
                                            {r.percentage}%
                                        </Percentage>
                                    </AnswerContent>
                                ))}
                            </AnswersWrapper>
                        </Details>
                    </DetailsContainer>
                </Content>
            </Body>
            <Footer>
                <ShareDrawer newsletterSignUpVisible={true} />
            </Footer>
        </Container>
        <NextAction />
    </TransitionContainerNoExit>
);

PollSuccess.propTypes = {
    questionText: PropTypes.string.isRequired,
    results: PropTypes.array.isRequired,
    isMobileResolution: PropTypes.bool,
    hasNextAction: PropTypes.object,
    noOfPeopleAnswered: PropTypes.string.isRequired,
    makeUppercase: PropTypes.bool.isRequired
};

export default connect(
    state => {
        return {
            questionText:
                state.poll.successInfo !== undefined
                    ? state.poll.successInfo.questionText
                    : 'Thanks for answering!',
            isMobileResolution: state.widget.isMobileResolution,
            hasNextAction: !!getNextAction(
                state.widget.actions,
                state.widget.activeAction.identifier,
                buildConditionalActionsAnswerHistory(state),
                state.widget.cards
            ),
            results:
                state.poll.successInfo.answers != null
                    ? state.poll.successInfo.answers
                          .filter(a => a.isOpen === false)
                          .map(p => ({
                              ...p,
                              percentage:
                                  state.poll.answerHistory !== undefined
                                      ? state.poll.answerHistory.find(
                                            r =>
                                                r.actionIdentifier ===
                                                state.poll.successInfo.actionIdentifier
                                        ) !== undefined
                                          ? state.poll.answerHistory
                                                .find(
                                                    r =>
                                                        r.actionIdentifier ===
                                                        state.poll.successInfo
                                                            .actionIdentifier
                                                )
                                                .results.find(
                                                    r => r.answerId === p.id
                                                ) !== undefined
                                              ? state.poll.answerHistory
                                                    .find(
                                                        r =>
                                                            r.actionIdentifier ===
                                                            state.poll.successInfo
                                                                .actionIdentifier
                                                    )
                                                    .results.find(
                                                        r => r.answerId === p.id
                                                    ).percentage
                                              : 0
                                          : 0
                                      : 0,
                              answerAnswered:
                                  state.poll.answerHistoryClicked !== undefined
                                      ? state.poll.answerHistoryClicked.find(
                                            r =>
                                                r.actionIdentifier ===
                                                state.poll.successInfo.actionIdentifier
                                        ) !== undefined
                                          ? state.poll.answerHistoryClicked
                                                .filter(
                                                    r =>
                                                        r.actionIdentifier ===
                                                        state.poll.successInfo
                                                            .actionIdentifier
                                                )
                                                .slice(-1)[0].answerId === p.id
                                          : state.poll.answerHistory !== undefined
                                          ? state.poll.answerHistory.find(
                                                r =>
                                                    r.actionIdentifier ===
                                                    state.poll.successInfo
                                                        .actionIdentifier
                                            ) !== undefined
                                              ? state.poll.answerHistory.find(
                                                    r =>
                                                        r.actionIdentifier ===
                                                        state.poll.successInfo
                                                            .actionIdentifier
                                                ).answerId === p.id
                                              : false
                                          : false
                                      : state.poll.answerHistory !== undefined
                                      ? state.poll.answerHistory.find(
                                            r =>
                                                r.actionIdentifier ===
                                                state.poll.successInfo.actionIdentifier
                                        ) !== undefined
                                          ? state.poll.answerHistory.find(
                                                r =>
                                                    r.actionIdentifier ===
                                                    state.poll.successInfo
                                                        .actionIdentifier
                                            ).answerId === p.id
                                          : false
                                      : false,
                              noOfAnswers: state.poll.successInfo.answers.filter(
                                  a => a.isOpen === false
                              ).length,
                              identifier: state.poll.successInfo.actionIdentifier
                          }))
                    : [],
            noOfPeopleAnswered: state.poll.successInfo.noOfAnswers
                ? (parseInt(state.poll.successInfo.noOfAnswers, 10) + 1).toLocaleString()
                : '',
            makeUppercase: (state.poll.successInfo.answers || []).every(
                a => !a.isOpen && a.text.length <= 8
            )
        };
    },
    {}
)(PollSuccess);



// WEBPACK FOOTER //
// ./src/containers/Poll/PollSuccess.jsx